// src/pages/VehicleRegisterExcel.js
import React, { useState, useEffect, useRef } from "react";
import ReactDOM from "react-dom";
import { useNavigate } from "react-router-dom";
import Select from "react-select";
import { useFormInput } from "@/utils";
import { Loading, Toast, Confirm } from "@/components";
import { useGetMyRentCorpQuery } from "@/redux/rentCorpApi";
import { usePostCarBulkFileMutation, usePostCarBulkMutation } from "@/redux/carApi";
import registerExcel from "@/assets/files/excel_register_sample.xlsx";

import successIcon from "@/assets/icons/icon-check_active.svg";
import failedIcon from "@/assets/icons/icon-error.svg";

export const VehicleRegisterExcel = () => {
    const navigate = useNavigate();
    const excelInputRef = useRef(null);
    const zipInputRef = useRef(null);

    const { formData, handleChange, setFormData } = useFormInput({
        toastMsg: "",
        confirmStatus: {
            confirmPopupStatus: false,
            confirmTitle: "",
            confirmDescription: "",
            confirmButtons: [],
        },
        rentCorpId: null,
        carBulkId: null, // postCarBulkFile API의 response.data.id 저장
    });
    const { toastMsg, confirmStatus, rentCorpId } = formData;

    // 사업자 리스트 조회
    const { data: myRentCorpData } = useGetMyRentCorpQuery();
    const corpOptions =
        myRentCorpData && myRentCorpData.data
            ? myRentCorpData.data.map((item) => ({
                  value: item.id,
                  label: item.corpName,
              }))
            : [];

    // 파일 상태
    const [excelFile, setExcelFile] = useState(null);
    const [zipFile, setZipFile] = useState(null);
    // 조회 결과 및 버튼 노출 여부
    const [bulkResult, setBulkResult] = useState(null);
    const [showResult, setShowResult] = useState(false);
    const [showBulkRegister, setShowBulkRegister] = useState(false);

    // RTK Query hooks
    const [postCarBulkFile, { isLoading: isBulkFileLoading }] = usePostCarBulkFileMutation();
    const [postCarBulk, { isLoading: isBulkLoading }] = usePostCarBulkMutation();

    // 엑셀 파일 업로드 (차량 데이터)
    const handleExcelFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setExcelFile(file);
        }
    };

    // 압축 파일 업로드 (차량 등록증 이미지, 선택)
    const handleZipFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setZipFile(file);
        }
    };

    // 엑셀 양식 다운로드 함수
    const handleDownloadTemplate = () => {
        const link = document.createElement("a");
        link.href = registerExcel;
        link.download = `차량등록_엑셀양식_${new Date().toISOString()}.xlsx`;
        link.click();
    };

    // "차량 업로드 조회" 버튼 클릭 – postCarBulkFile API 호출
    const handleBulkFileUpload = async () => {
        if (!rentCorpId) {
            setFormData((prev) => ({
                ...prev,
                toastMsg: "사업자 선택은 필수입니다.",
            }));
            return;
        }
        if (!excelFile) {
            setFormData((prev) => ({
                ...prev,
                toastMsg: "차량 데이터 엑셀 파일을 업로드 해주세요.",
            }));
            return;
        }
        const formDataObj = new FormData();
        formDataObj.append("excel_file", excelFile);
        if (zipFile) {
            formDataObj.append("zip_file", zipFile);
        }
        try {
            // 타임아웃은 axios 설정에서 별도로 조정 (예: 10분)
            const response = await postCarBulkFile({ rentCorpId, body: formDataObj }).unwrap();
            // response.data.id 저장 및 결과 테이블 데이터 매핑
            console.log(response.data.id);
            setFormData((prev) => ({ ...prev, carBulkId: response.data.id }));
            setBulkResult(response.data);
            setShowResult(true);
            setShowBulkRegister(true);
        } catch (error) {
            setFormData((prev) => ({
                ...prev,
                toastMsg: error?.data?.message || "차량 엑셀 업로드 실패",
            }));
        }
    };

    // "차량 일괄 등록" 버튼 클릭 – postCarBulk API 호출
    const handleBulkRegister = async () => {
        const successCount = bulkResult?.carBulkDetails.filter((detail) => detail.successYn === "Y").length;
        if (successCount === 0) {
            setFormData((prev) => ({
                ...prev,
                toastMsg: "등록할 수 있는 차량 데이터가 없습니다.",
            }));
            return;
        }

        if (!rentCorpId || !formData.carBulkId) {
            setFormData((prev) => ({
                ...prev,
                toastMsg: "엑셀 업로드를 먼저 진행해주세요.",
            }));
            return;
        }
        try {
            await postCarBulk({ carBulkId: formData.carBulkId }).unwrap();
            setFormData((prev) => ({
                ...prev,
                confirmStatus: {
                    confirmPopupStatus: true,
                    confirmTitle: "차량 일괄 등록이 완료되었습니다.",
                    confirmDescription: "",
                    confirmButtons: [
                        {
                            label: "확인",
                            className: "btn-primary",
                            onClick: () => {
                                setFormData((prev2) => ({
                                    ...prev2,
                                    confirmStatus: { confirmPopupStatus: false },
                                }));
                                navigate("/vehicle/manage");
                            },
                        },
                    ],
                },
            }));
        } catch (error) {
            setFormData((prev) => ({
                ...prev,
                toastMsg: error?.data?.message || "차량 일괄 등록 실패",
            }));
        }
    };

    // CSV 다운로드 (각 테이블 데이터를 CSV로 변환)
    const handleDownloadExcel = (section, data) => {
        if (!data || data.length === 0) {
            setFormData((prev) => ({
                ...prev,
                toastMsg: "다운로드할 데이터가 없습니다.",
            }));
            return;
        }
        // CSV 형식: 각 객체의 값들을 콤마로 연결하고 줄바꿈
        const csvContent = "data:text/csv;charset=utf-8," + data.map((row) => Object.values(row).join(",")).join("\n");
        const encodedUri = encodeURI(csvContent);
        const link = document.createElement("a");
        link.setAttribute("href", encodedUri);
        const fileName = `${section}_${new Date().toISOString()}.csv`;
        link.setAttribute("download", fileName);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    // 드래그 오버 이벤트: 기본 동작 방지
    const handleDragOver = (e) => {
        e.preventDefault();
        e.stopPropagation();
    };

    // 엑셀 파일 드랍 처리
    const handleDropExcel = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
            const file = e.dataTransfer.files[0];
            const fileName = file.name.toLowerCase();
            if (fileName.endsWith(".xlsx") || fileName.endsWith(".xls")) {
                setExcelFile(file);
            } else {
                alert("엑셀 파일만 업로드 가능합니다.");
            }
            e.dataTransfer.clearData();
        }
    };

    // 압축 파일 드랍 처리
    const handleDropZip = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
            const file = e.dataTransfer.files[0];
            if (file.name.toLowerCase().endsWith(".zip")) {
                setZipFile(file);
            } else {
                alert("압축 파일(.zip)만 업로드 가능합니다.");
            }
            e.dataTransfer.clearData();
        }
    };

    // drag-area 클릭 시 hidden input 클릭 트리거
    const handleExcelAreaClick = () => {
        if (excelInputRef.current) {
            excelInputRef.current.click();
        }
    };

    const handleZipAreaClick = () => {
        if (zipInputRef.current) {
            zipInputRef.current.click();
        }
    };

    return (
        <>
            {formData.toastMsg && <Toast visible={true} msg={formData.toastMsg} setFormData={setFormData} />}

            <div className="management-form container">
                <div className="form-container">
                    <div className="form-head">
                        <div className="head-tit">
                            <span className="form_name">차량 엑셀 일괄 등록</span>
                            <span className="form_desc"></span>
                        </div>
                    </div>

                    <div className="form-option">
                        <div className="option-item">
                            <div className="item-content">
                                <div className="item-head">
                                    <span className="head-tit">사업자 선택</span>
                                </div>
                                <div className="item-input">
                                    <Select
                                        options={corpOptions}
                                        className="custom-select"
                                        classNamePrefix="custom-select"
                                        isSearchable={false}
                                        placeholder="사업자 선택"
                                        value={corpOptions.find((opt) => opt.value === formData.rentCorpId)}
                                        onChange={(option) =>
                                            setFormData((prev) => ({
                                                ...prev,
                                                rentCorpId: option.value,
                                            }))
                                        }
                                    />
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="form-content">
                        <div className="upload-content">
                            <div className="upload-license">
                                <div className="upload-box box-upload">
                                    <div className="box-txt">
                                        <p>차량 데이터가 입력된 엑셀 파일을 업로드해주세요.</p>
                                        <p className="sub">차량 데이터는 양식에 알맞게 기입되어야 합니다.</p>
                                    </div>
                                    <div
                                        className={`drag-area ${excelFile ? "file-attached" : ""}`}
                                        data-filename={excelFile ? excelFile.name : "파일을 드래그 혹은 클릭하여 첨부해주세요."}
                                        onDragOver={handleDragOver}
                                        onDrop={handleDropExcel}
                                        onClick={handleExcelAreaClick}
                                    >
                                        {/* hidden input */}
                                        <input
                                            ref={excelInputRef}
                                            type="file"
                                            accept=".xlsx,.xls"
                                            style={{ display: "none" }}
                                            onChange={handleExcelFileChange}
                                        />
                                    </div>
                                </div>

                                <div className="upload-box box-download">
                                    <div className="box-txt">
                                        <p>
                                            차량 등록을 위해 차량 등록증 파일(압축파일)을 업로드해주세요.
                                            <br />
                                            (선택 사항)
                                        </p>
                                        <p className="sub">ex) 12가1234.zip</p>
                                    </div>
                                    <div
                                        className={`drag-area ${zipFile ? "file-attached" : ""}`}
                                        data-filename={zipFile ? zipFile.name : "파일을 드래그 혹은 클릭하여 첨부해주세요."}
                                        onDragOver={handleDragOver}
                                        onDrop={handleDropZip}
                                        onClick={handleZipAreaClick}
                                    >
                                        {/* hidden input */}
                                        <input
                                            ref={zipInputRef}
                                            type="file"
                                            accept=".zip"
                                            style={{ display: "none" }}
                                            onChange={handleZipFileChange}
                                        />
                                    </div>
                                </div>

                                {bulkResult && (
                                    <div className="upload-box box-result">
                                        <div className="box-txt">
                                            <p>조회현황</p>
                                        </div>
                                        <div className="result-list">
                                            <div className="item">
                                                <span className="item-tit">조회 성공</span>
                                                <span className="item-desc">{bulkResult?.successCount} 대</span>
                                            </div>
                                            <div className="item">
                                                <span className="item-tit">조회 실패</span>
                                                <span className="item-desc">{bulkResult?.searchFailCount} 대</span>
                                            </div>
                                            <div className="item">
                                                <span className="item-tit">차량번호 중복</span>
                                                <span className="item-desc">{bulkResult?.duplicateCount} 대</span>
                                            </div>
                                            <div className="item">
                                                <span className="item-tit">기타 실패</span>
                                                <span className="item-desc">{bulkResult?.etcFailCount} 대</span>
                                            </div>
                                            <div className="item total">
                                                <span className="item-tit">Total</span>
                                                <span className="item-desc">{bulkResult?.totalCount} 대</span>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>

                            {bulkResult && (
                                <div className="upload-result">
                                    <div className="upload-head">
                                        <span className="tit-main">차량 업로드 조회 결과</span>
                                        <span className="tit-sub">차량 업로드 조회 결과입니다.</span>
                                    </div>

                                    {/* 조회 성공 테이블 */}
                                    <div className="result-success">
                                        <div className="table-container">
                                            <div className="table-head">
                                                <span className="head-tit">조회 성공</span>
                                                <div className="table-buttons">
                                                    <button
                                                        type="button"
                                                        className="btn-table_option btn-download"
                                                        onClick={() =>
                                                            handleDownloadExcel(
                                                                "success",
                                                                bulkResult.carBulkDetails.filter((detail) => detail.successYn === "Y")
                                                            )
                                                        }
                                                    >
                                                        엑셀 다운로드
                                                    </button>
                                                </div>
                                            </div>
                                            <div className="table-content">
                                                <div className="table-wrapper">
                                                    <div className="table">
                                                        <table>
                                                            <colgroup>
                                                                <col width="5%" />
                                                                <col width="5%" />
                                                                <col width="5%" />
                                                                <col width="5%" />
                                                                <col width="5%" />
                                                                <col width="5%" />
                                                                <col width="5%" />
                                                                <col width="5%" />
                                                                <col width="5%" />
                                                                <col width="5%" />
                                                                <col width="5%" />
                                                                <col width="5%" />
                                                            </colgroup>
                                                            <thead>
                                                                <tr>
                                                                    <th>
                                                                        <div className="cell">
                                                                            <span className="cell-tit">조회상태</span>
                                                                        </div>
                                                                    </th>
                                                                    <th>
                                                                        <div className="cell">
                                                                            <span className="cell-tit">차량번호</span>
                                                                        </div>
                                                                    </th>
                                                                    <th>
                                                                        <div className="cell">
                                                                            <span className="cell-tit">소유주</span>
                                                                        </div>
                                                                    </th>
                                                                    <th>
                                                                        <div className="cell">
                                                                            <span className="cell-tit">차종</span>
                                                                        </div>
                                                                    </th>
                                                                    <th>
                                                                        <div className="cell">
                                                                            <span className="cell-tit">제조사</span>
                                                                        </div>
                                                                    </th>
                                                                    <th>
                                                                        <div className="cell">
                                                                            <span className="cell-tit">모델</span>
                                                                        </div>
                                                                    </th>
                                                                    <th>
                                                                        <div className="cell">
                                                                            <span className="cell-tit">상세모델</span>
                                                                        </div>
                                                                    </th>
                                                                    <th>
                                                                        <div className="cell">
                                                                            <span className="cell-tit">변속기</span>
                                                                        </div>
                                                                    </th>
                                                                    <th>
                                                                        <div className="cell">
                                                                            <span className="cell-tit">연식</span>
                                                                        </div>
                                                                    </th>
                                                                    <th>
                                                                        <div className="cell">
                                                                            <span className="cell-tit">주행거리</span>
                                                                        </div>
                                                                    </th>
                                                                    <th>
                                                                        <div className="cell">
                                                                            <span className="cell-tit">유종</span>
                                                                        </div>
                                                                    </th>
                                                                    <th>
                                                                        <div className="cell">
                                                                            <span className="cell-tit">색상</span>
                                                                        </div>
                                                                    </th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {bulkResult.carBulkDetails.filter((detail) => detail.successYn === "Y")
                                                                    .length > 0 ? (
                                                                    bulkResult.carBulkDetails
                                                                        .filter((detail) => detail.successYn === "Y")
                                                                        .map((detail, index) => (
                                                                            <tr key={index}>
                                                                                <td>
                                                                                    <div className="cell" data-align="center">
                                                                                        <span className="cell-icon">
                                                                                            <img
                                                                                                className="icon-success"
                                                                                                src={successIcon}
                                                                                                alt=""
                                                                                            />
                                                                                        </span>
                                                                                    </div>
                                                                                </td>
                                                                                <td>
                                                                                    <div className="cell" data-align="center">
                                                                                        <span className="cell-desc">
                                                                                            {detail.emeyeSearchHistory?.carData?.carnum ||
                                                                                                "-"}
                                                                                        </span>
                                                                                    </div>
                                                                                </td>
                                                                                <td>
                                                                                    <div className="cell" data-align="center">
                                                                                        <span className="cell-desc">
                                                                                            {detail.emeyeSearchHistory?.carData?.regname ||
                                                                                                "-"}
                                                                                        </span>
                                                                                    </div>
                                                                                </td>
                                                                                <td>
                                                                                    <div className="cell" data-align="center">
                                                                                        <span className="cell-desc">
                                                                                            {detail.emeyeSearchHistory?.carData?.cargubun ||
                                                                                                "-"}
                                                                                        </span>
                                                                                    </div>
                                                                                </td>
                                                                                <td>
                                                                                    <div className="cell" data-align="center">
                                                                                        <span className="cell-desc">
                                                                                            {detail.emeyeSearchHistory?.carData
                                                                                                ?.makername || "-"}
                                                                                        </span>
                                                                                    </div>
                                                                                </td>
                                                                                <td>
                                                                                    <div className="cell" data-align="center">
                                                                                        <span className="cell-desc">
                                                                                            {detail.emeyeSearchHistory?.carData
                                                                                                ?.modelname || "-"}
                                                                                        </span>
                                                                                    </div>
                                                                                </td>
                                                                                <td>
                                                                                    <div className="cell" data-align="center">
                                                                                        <span className="cell-desc">
                                                                                            {detail.emeyeSearchHistory?.carData
                                                                                                ?.classname || "-"}
                                                                                        </span>
                                                                                    </div>
                                                                                </td>
                                                                                <td>
                                                                                    <div className="cell" data-align="center">
                                                                                        <span className="cell-desc">오토</span>
                                                                                    </div>
                                                                                </td>
                                                                                <td>
                                                                                    <div className="cell" data-align="center">
                                                                                        <span className="cell-desc">
                                                                                            {detail.emeyeSearchHistory?.carData?.year ||
                                                                                                "-"}
                                                                                        </span>
                                                                                    </div>
                                                                                </td>
                                                                                <td>
                                                                                    <div className="cell" data-align="center">
                                                                                        <span className="cell-desc">
                                                                                            {detail.emeyeSearchHistory?.carData
                                                                                                ?.stdmileage || "-"}
                                                                                        </span>
                                                                                    </div>
                                                                                </td>
                                                                                <td>
                                                                                    <div className="cell" data-align="center">
                                                                                        <span className="cell-desc">
                                                                                            {detail.emeyeSearchHistory?.carData?.fuel ||
                                                                                                "-"}
                                                                                        </span>
                                                                                    </div>
                                                                                </td>
                                                                                <td>
                                                                                    <div className="cell" data-align="center">
                                                                                        <span className="cell-desc">
                                                                                            {detail.emeyeSearchHistory?.carData?.color ||
                                                                                                "-"}
                                                                                        </span>
                                                                                    </div>
                                                                                </td>
                                                                            </tr>
                                                                        ))
                                                                ) : (
                                                                    <tr>
                                                                        <td colSpan="12" className="data-empty">
                                                                            <div className="cell" data-align="center">
                                                                                <span className="cell-desc">데이터가 없습니다.</span>
                                                                            </div>
                                                                        </td>
                                                                    </tr>
                                                                )}
                                                            </tbody>
                                                        </table>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    {/* 조회 실패 테이블 */}
                                    <div className="result-failed">
                                        <div className="table-container">
                                            <div className="table-head">
                                                <span className="head-tit">
                                                    조회 실패{" "}
                                                    <i>
                                                        아래와 같은 원인으로 조회에 실패했습니다. 다시 시도하거나 차량 직접 등록을
                                                        진행해주세요.
                                                    </i>
                                                </span>
                                                <div className="table-buttons">
                                                    <button
                                                        type="button"
                                                        className="btn-table_option btn-download"
                                                        onClick={() =>
                                                            handleDownloadExcel(
                                                                "fail",
                                                                bulkResult.carBulkDetails.filter((detail) => detail.successYn !== "Y")
                                                            )
                                                        }
                                                    >
                                                        엑셀 다운로드
                                                    </button>
                                                </div>
                                            </div>
                                            <div className="table-content">
                                                <div className="table-wrapper">
                                                    <div className="table">
                                                        <table>
                                                            <colgroup>
                                                                <col width="8.5%" />
                                                                <col width="8.5%" />
                                                                <col width="8.5%" />
                                                                <col width="82%" />
                                                            </colgroup>
                                                            <thead>
                                                                <tr>
                                                                    <th>
                                                                        <div className="cell">
                                                                            <span className="cell-tit">조회 상태</span>
                                                                        </div>
                                                                    </th>
                                                                    <th>
                                                                        <div className="cell">
                                                                            <span className="cell-tit">차량번호</span>
                                                                        </div>
                                                                    </th>
                                                                    <th>
                                                                        <div className="cell">
                                                                            <span className="cell-tit">소유주</span>
                                                                        </div>
                                                                    </th>
                                                                    <th>
                                                                        <div className="cell">
                                                                            <span className="cell-tit">조회 실패 원인</span>
                                                                        </div>
                                                                    </th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {bulkResult.carBulkDetails.filter((detail) => detail.successYn !== "Y")
                                                                    .length > 0 ? (
                                                                    bulkResult.carBulkDetails
                                                                        .filter((detail) => detail.successYn !== "Y")
                                                                        .map((detail, idx) => (
                                                                            <tr key={idx}>
                                                                                <td>
                                                                                    <div className="cell" data-align="center">
                                                                                        <span className="cell-icon">
                                                                                            <img
                                                                                                className="icon-failed"
                                                                                                src={failedIcon}
                                                                                                alt=""
                                                                                            />
                                                                                        </span>
                                                                                    </div>
                                                                                </td>
                                                                                <td>
                                                                                    <div className="cell" data-align="center">
                                                                                        <span className="cell-desc">
                                                                                            {detail.carNumber || "-"}
                                                                                        </span>
                                                                                    </div>
                                                                                </td>
                                                                                <td>
                                                                                    <div className="cell" data-align="center">
                                                                                        <span className="cell-desc">
                                                                                            {detail.ownerName || "-"}
                                                                                        </span>
                                                                                    </div>
                                                                                </td>
                                                                                <td>
                                                                                    <div className="cell" data-align="left">
                                                                                        <span className="cell-desc color-error">
                                                                                            {detail.failReason || "조회 실패"}
                                                                                        </span>
                                                                                    </div>
                                                                                </td>
                                                                            </tr>
                                                                        ))
                                                                ) : (
                                                                    <tr>
                                                                        <td colSpan="4" className="data-empty">
                                                                            <div className="cell" data-align="center">
                                                                                <span className="cell-desc">데이터가 없습니다.</span>
                                                                            </div>
                                                                        </td>
                                                                    </tr>
                                                                )}
                                                            </tbody>
                                                        </table>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* 업로드/등록 버튼 영역 */}
                            <div className="upload-button">
                                {!showResult && (
                                    <button type="button" className="btn btn-sm btn-fourth" onClick={handleBulkFileUpload}>
                                        차량 업로드 조회
                                    </button>
                                )}
                                {showBulkRegister && (
                                    <button type="button" className="btn btn-sm btn-fourth" onClick={handleBulkRegister}>
                                        차량 일괄 등록
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {confirmStatus.confirmPopupStatus && (
                <Confirm
                    layer={true}
                    onClose={() =>
                        setFormData((prev) => ({
                            ...prev,
                            confirmStatus: { confirmPopupStatus: false },
                        }))
                    }
                    title={confirmStatus.confirmTitle}
                    description={confirmStatus.confirmDescription}
                    buttons={confirmStatus.confirmButtons}
                />
            )}
        </>
    );
};

export default VehicleRegisterExcel;
